import { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  DEFAULT_MAP_CENTER,
  hasLocationCoordinates,
  parseCoord,
} from '@/lib/google-maps';
import { useFestivalWorkspaceStore } from '@/stores/festival-workspace-store';
import { InteractiveMap } from './InteractiveMap';

function getInitialMapChoice(location, locationName) {
  if (hasLocationCoordinates(location)) {
    return 'yes';
  }
  if (locationName?.trim()) {
    return 'no';
  }
  return undefined;
}

export function LocationMapField({
  shouldPromptForMap = false,
  location,
  locationName = '',
  onLocationChange,
  onLocationNameChange,
  readOnly = false,
  defaultZoom,
  ...rest
}) {
  const festival = useFestivalWorkspaceStore((s) => s.festival);

  const [mapChoice, setMapChoice] = useState(() =>
    shouldPromptForMap ? getInitialMapChoice(location, locationName) : 'yes',
  );

  const defaultCenter = useMemo(
    () => ({
      lat: parseCoord(festival?.location?.latitude, DEFAULT_MAP_CENTER.lat),
      lng: parseCoord(festival?.location?.longitude, DEFAULT_MAP_CENTER.lng),
    }),
    [festival?.location?.latitude, festival?.location?.longitude],
  );

  const mapField = (
    <InteractiveMap
      className="location-map-field"
      location={location}
      locationName={locationName}
      onLocationChange={onLocationChange}
      onLocationNameChange={onLocationNameChange}
      readOnly={readOnly}
      showSearch
      geocodeOnPlace
      defaultCenter={defaultCenter}
      defaultZoom={defaultZoom}
      {...rest}
    />
  );

  if (!shouldPromptForMap) {
    return <div className="mb-3">{mapField}</div>;
  }

  if (readOnly) {
    if (hasLocationCoordinates(location)) {
      return <div className="mb-3">{mapField}</div>;
    }

    return (
      <div className="mb-3 space-y-2">
        <Label htmlFor="location-description">
          Please describe the location
        </Label>
        <Input
          id="location-description"
          type="text"
          value={locationName}
          disabled
        />
      </div>
    );
  }

  return (
    <div className="mb-3 space-y-3">
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">
          Would you like to pin this location on a map?
        </legend>
        <RadioGroup
          value={mapChoice ?? ''}
          onValueChange={setMapChoice}
          className="flex gap-4"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="yes" id="location-map-yes" />
            <Label htmlFor="location-map-yes">Yes</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="no" id="location-map-no" />
            <Label htmlFor="location-map-no">No</Label>
          </div>
        </RadioGroup>
      </fieldset>

      {mapChoice === 'yes' ? mapField : null}

      {mapChoice === 'no' ? (
        <div className="space-y-2">
          <Label htmlFor="location-description">
            Please describe the location
          </Label>
          <Input
            id="location-description"
            type="text"
            value={locationName}
            onChange={(event) => onLocationNameChange?.(event.target.value)}
          />
        </div>
      ) : null}
    </div>
  );
}
